import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

function formatDate(dateValue) {
  if (!dateValue) return "Not configured yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

const EMPTY_FORM = {
  booking_engine_mode: "none",
  status: "draft",
};

export default function OnboardingSetupAdmin() {
  const currentUser = useMemo(() => readStoredUser(), []);
  const isAdminRole = Number(currentUser?.id) === 1;

  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [feedback, setFeedback] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingBusinessUnitId, setSavingBusinessUnitId] = useState(null);

  const loadSetupList = async ({ keepSelection = true } = {}) => {
    setLoading(true);
    setFeedback("");

    try {
      const response = await axios.get("/api/onboarding/admin/setup");
      const rows = Array.isArray(response.data) ? response.data : [];
      setBusinessUnits(rows);

      if (!keepSelection) {
        setSelectedBusinessUnitId(null);
        setSelectedBusinessUnit(null);
        setFormState(EMPTY_FORM);
        return;
      }

      if (selectedBusinessUnitId) {
        const matchingRow = rows.find(
          (row) => Number(row.business_unit_id) === Number(selectedBusinessUnitId)
        );
        if (matchingRow) {
          setSelectedBusinessUnit(matchingRow);
        }
      }
    } catch (error) {
      console.error("[Admin][OnboardingSetup] Error loading setup list:", error);
      setFeedback("Could not load onboarding setup data.");
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessUnitDetail = async (businessUnitId) => {
    setSelectedBusinessUnitId(businessUnitId);
    setDetailLoading(true);
    setFeedback("");

    try {
      const response = await axios.get(`/api/onboarding/admin/setup/${businessUnitId}`);
      const detail = response.data || null;
      setSelectedBusinessUnit(detail);
      setFormState({
        booking_engine_mode: detail?.setup?.booking_engine_mode || "none",
        status: detail?.setup?.status || "draft",
      });
    } catch (error) {
      console.error("[Admin][OnboardingSetup] Error loading setup detail:", error);
      setSelectedBusinessUnit(null);
      setFormState(EMPTY_FORM);
      setFeedback(error?.response?.data?.error || "Could not load the selected onboarding setup.");
    } finally {
      setDetailLoading(false);
    }
  };

  const saveSelectedBusinessUnit = async () => {
    if (!selectedBusinessUnitId) return;

    setSavingBusinessUnitId(selectedBusinessUnitId);
    setFeedback("");

    try {
      const response = await axios.put(`/api/onboarding/admin/setup/${selectedBusinessUnitId}`, formState);
      const detail = response.data || null;
      setSelectedBusinessUnit((current) => ({
        ...(current || {}),
        ...detail,
        setup: detail?.setup || null,
        ready: Boolean(detail?.ready),
      }));
      setFeedback(
        detail?.ready
          ? "Onboarding prepared. Hotel users can now access the onboarding forms."
          : "Onboarding saved as draft. Hotel users will stay blocked until you mark it as ready."
      );
      await loadSetupList();
      await loadBusinessUnitDetail(selectedBusinessUnitId);
    } catch (error) {
      console.error("[Admin][OnboardingSetup] Error saving setup:", error);
      setFeedback(error?.response?.data?.error || "Could not save the onboarding setup.");
    } finally {
      setSavingBusinessUnitId(null);
    }
  };

  useEffect(() => {
    loadSetupList({ keepSelection: false });
  }, []);

  if (!isAdminRole) {
    return (
      <div className="adminCard" style={{ margin: "24px" }}>
        <div className="adminCardHeader">
          <h2 className="adminCardTitle">Onboarding Setup</h2>
          <div className="adminCardSubtitle">Admin access only</div>
        </div>
        <div className="adminCardInner" style={{ color: "#94a3b8" }}>
          This area is reserved for admin users because it controls whether a hotel can start onboarding.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
      <div
        style={{
          borderRadius: "22px",
          padding: "22px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "radial-gradient(780px 240px at 0% 0%, rgba(92,191,255,0.12), transparent 60%), rgba(10,15,28,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "#8ea0bc" }}>
          Admin onboarding bootstrap
        </div>
        <h2 style={{ color: "#fff", margin: "8px 0 10px" }}>Onboarding Setup</h2>
        <p style={{ color: "#b9c3d7", maxWidth: "880px", margin: 0, lineHeight: 1.6 }}>
          Define the onboarding prerequisites that the hotel user should not decide. This stays in staging only and
          controls access to the onboarding forms without touching live PMS or Purchasing data.
        </p>
      </div>

      {feedback ? (
        <div
          style={{
            borderRadius: "14px",
            padding: "12px 14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: feedback.toLowerCase().includes("could not") ? "#542929" : "#244428",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {feedback}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)",
          gap: "18px",
        }}
      >
        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <div>
              <h3 style={{ color: "#fff", margin: 0 }}>Hotels</h3>
              <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                Choose the business unit you want to prepare before users enter onboarding.
              </p>
            </div>
            <button
              type="button"
              onClick={() => loadSetupList()}
              disabled={loading}
              style={{
                borderRadius: "12px",
                border: "none",
                padding: "10px 14px",
                background: "#5cbfff",
                color: "#08111e",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {loading ? (
              <div style={{ color: "#9db0cf" }}>Loading business units...</div>
            ) : businessUnits.length === 0 ? (
              <div style={{ color: "#9db0cf" }}>No business units available.</div>
            ) : (
              businessUnits.map((row) => {
                const isSelected = Number(selectedBusinessUnitId) === Number(row.business_unit_id);
                const ready = row?.setup?.status === "ready";
                return (
                  <article
                    key={row.business_unit_id}
                    style={{
                      borderRadius: "18px",
                      padding: "16px",
                      border: `1px solid ${isSelected ? "rgba(92,191,255,0.45)" : "rgba(255,255,255,0.08)"}`,
                      background: isSelected ? "rgba(92,191,255,0.08)" : "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <div>
                        <h4 style={{ color: "#fff", margin: 0 }}>{row.business_unit_name}</h4>
                        <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                          {row?.setup?.booking_engine_mode === "pilcore"
                            ? "Pilcore Booking Engine included"
                            : "No Booking Engine included"}
                        </p>
                      </div>
                      <div
                        style={{
                          alignSelf: "flex-start",
                          borderRadius: "999px",
                          padding: "6px 10px",
                          background: ready ? "rgba(80,190,120,0.18)" : "rgba(255,183,77,0.16)",
                          color: ready ? "#9ff0bb" : "#ffd27f",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}
                      >
                        {ready ? "Ready" : "Draft"}
                      </div>
                    </div>

                    <div style={{ marginTop: "12px", color: "#9db0cf", fontSize: "0.9rem", lineHeight: 1.5 }}>
                      <div>Last update: {formatDate(row?.setup?.updated_at)}</div>
                    </div>

                    <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => loadBusinessUnitDetail(row.business_unit_id)}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#1b2c43",
                          color: "#fff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Configure
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          {!selectedBusinessUnitId ? (
            <div style={{ color: "#9db0cf" }}>Select a hotel to define its onboarding access rules.</div>
          ) : detailLoading ? (
            <div style={{ color: "#9db0cf" }}>Loading onboarding setup...</div>
          ) : !selectedBusinessUnit ? (
            <div style={{ color: "#9db0cf" }}>No onboarding setup detail loaded.</div>
          ) : (
            <>
              <div style={{ marginBottom: "16px" }}>
                <h3 style={{ color: "#fff", margin: 0 }}>{selectedBusinessUnit.business_unit_name}</h3>
                <p style={{ color: "#9db0cf", margin: "6px 0 0", lineHeight: 1.6 }}>
                  Decide whether this hotel includes Pilcore Booking Engine and whether the hotel user can already
                  enter the onboarding forms.
                </p>
              </div>

              <div style={{ display: "grid", gap: "16px" }}>
                <section
                  style={{
                    borderRadius: "18px",
                    padding: "16px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div style={{ color: "#fff", fontWeight: 700, marginBottom: "10px" }}>Pilcore Booking Engine</div>
                  <div style={{ color: "#9db0cf", marginBottom: "14px", lineHeight: 1.6 }}>
                    This is an admin-side contracting decision. The hotel user will not be asked this inside onboarding.
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px" }}>
                    {[
                      {
                        value: "pilcore",
                        title: "Included",
                        copy: "This business unit should behave as contracted with Pilcore Booking Engine.",
                      },
                      {
                        value: "none",
                        title: "Not included",
                        copy: "The onboarding will continue without Pilcore Booking Engine assumptions.",
                      },
                    ].map((option) => {
                      const isActive = formState.booking_engine_mode === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            setFormState((current) => ({
                              ...current,
                              booking_engine_mode: option.value,
                            }))
                          }
                          style={{
                            textAlign: "left",
                            borderRadius: "16px",
                            padding: "14px 16px",
                            border: `1px solid ${isActive ? "rgba(92,191,255,0.45)" : "rgba(255,255,255,0.08)"}`,
                            background: isActive ? "rgba(92,191,255,0.1)" : "rgba(255,255,255,0.03)",
                            color: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{option.title}</div>
                          <div style={{ marginTop: "6px", color: "#9db0cf", lineHeight: 1.5 }}>{option.copy}</div>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section
                  style={{
                    borderRadius: "18px",
                    padding: "16px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div style={{ color: "#fff", fontWeight: 700, marginBottom: "10px" }}>Onboarding access</div>
                  <div style={{ color: "#9db0cf", marginBottom: "14px", lineHeight: 1.6 }}>
                    Only when this is marked as ready will the hotel user be able to enter the onboarding forms.
                  </div>

                  <label style={{ display: "block", color: "#dbe4f0", fontWeight: 600, marginBottom: "8px" }}>
                    Status
                  </label>
                  <select
                    value={formState.status}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      padding: "12px 14px",
                      background: "#07101b",
                      color: "#fff",
                    }}
                  >
                    <option value="draft">Draft - keep onboarding blocked</option>
                    <option value="ready">Ready - hotel user can start onboarding</option>
                  </select>

                  <div style={{ marginTop: "14px", color: "#9db0cf", fontSize: "0.92rem", lineHeight: 1.6 }}>
                    Current live impact: none. This only affects onboarding access and onboarding staging forms.
                  </div>
                </section>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => loadBusinessUnitDetail(selectedBusinessUnitId)}
                    style={{
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      padding: "10px 14px",
                      background: "transparent",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={saveSelectedBusinessUnit}
                    disabled={savingBusinessUnitId === selectedBusinessUnitId}
                    style={{
                      borderRadius: "12px",
                      border: "none",
                      padding: "10px 14px",
                      background: "#ff975c",
                      color: "#08111e",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {savingBusinessUnitId === selectedBusinessUnitId ? "Saving..." : "Save onboarding setup"}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
